import { Info } from 'lucide-react'
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './infotip.css'
import { Modal } from './Modal'
import { usePopover } from './usePopover'

/**
 * A small ⓘ that reveals explanatory text on click — the "why this exists"
 * prose for a page, or for a field whose label may only name it.
 *
 * `size="inline"` is the bare glyph that rides beside a field label: no border,
 * no control height, sized to the text it follows. A label names its control and
 * nothing more — everything that explains, warns or gives an example goes in
 * here, where it is available on demand and costs the form no height.
 *
 * ## It opens in place, and becomes a Modal only when it cannot
 *
 * A sentence about a field should not cover the field. So the panel is anchored
 * to the glyph — portalled to the body, so no Sheet, band or scroll region can
 * clip it; placed on whichever side of the trigger has more room; aligned to the
 * inline START of the trigger, which mirrors with writing direction rather than
 * being pinned to an edge that is only correct in one of them.
 *
 * Where an anchored panel genuinely cannot hold the prose — a paragraph on a
 * phone — it renders as a Modal instead. That is **measured at open**, not
 * decided by a screen width or a character count: the panel is laid out, its
 * content measured against the room actually left, and the answer follows. A
 * threshold would be wrong from both directions on the same day.
 */

/** The reading measure, and the air the panel keeps from the window's edge. */
const MAX_W = 22 * 16
const GUTTER = 12

type Placement = { top: number; left: number; width: number }

export function InfoTip({
  children,
  label = 'About this section',
  size = 'control',
  trigger,
}: {
  children: ReactNode
  label?: string
  size?: 'control' | 'inline' | 'bare'
  /**
   * What opens it, where the ⓘ is not the right mark.
   *
   * A note's marker is the worked case: the number IS the affordance, and an
   * ⓘ beside it would be a second thing to press for the same panel. The
   * behavior — anchored, portalled, Modal only where the prose will not fit —
   * is the part worth sharing, and it is why this takes a trigger rather than
   * a second popover being written.
   */
  trigger?: ReactNode
}) {
  const { open, setOpen, ref, panelRef } = usePopover()
  const btnRef = useRef<HTMLButtonElement>(null)
  /** Null while measuring; a placement once it fits, or `false` for the Modal. */
  const [placed, setPlaced] = useState<Placement | false | null>(null)

  useLayoutEffect(() => {
    if (!open) {
      setPlaced(null)
      return
    }
    const trigger = btnRef.current
    const panel = panelRef.current
    if (!trigger || !panel) return

    const rect = trigger.getBoundingClientRect()
    const width = Math.min(MAX_W, window.innerWidth - GUTTER * 2)
    const below = window.innerHeight - rect.bottom - GUTTER * 2
    const above = rect.top - GUTTER * 2
    const room = Math.max(below, above)

    // Measured at the width it will actually be shown at, or the answer is
    // about a panel nobody is going to see.
    panel.style.width = `${width}px`
    const needed = panel.scrollHeight
    if (needed > room) {
      setPlaced(false)
      return
    }

    const rtl = getComputedStyle(trigger).direction === 'rtl'
    const start = rtl ? rect.right - width : rect.left
    setPlaced({
      top:
        below >= above
          ? rect.bottom + GUTTER / 2
          : rect.top - needed - GUTTER / 2,
      // Nudged by the smallest amount that brings it back on screen — the same
      // rule every floating thing here follows.
      left: Math.min(
        Math.max(start, GUTTER),
        window.innerWidth - width - GUTTER,
      ),
      width,
    })
  }, [open, panelRef])

  const prose = <div className="infotip__prose">{children}</div>

  return (
    <span className="infotip" ref={ref}>
      <button
        type="button"
        ref={btnRef}
        className={`infotip__btn infotip__btn--${size}`}
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {trigger ?? <Info size={size === 'inline' ? 13 : 15} aria-hidden />}
      </button>
      {open && placed === false ? (
        <Modal title={label} ariaLabel={label} onClose={() => setOpen(false)}>
          {prose}
        </Modal>
      ) : null}
      {open && placed !== false
        ? createPortal(
            <div
              className="infotip__pop"
              ref={panelRef}
              role="dialog"
              aria-label={label}
              /* Hidden for the measuring pass rather than unmounted: it has to
                 be laid out to be measured, and a panel that appears at one
                 place and jumps to another is worse than one that waits. */
              style={
                placed
                  ? { top: placed.top, left: placed.left, width: placed.width }
                  : { top: 0, left: 0, visibility: 'hidden' }
              }
            >
              {prose}
            </div>,
            document.body,
          )
        : null}
    </span>
  )
}
